import { isEmpty, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import React from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta17Model,
  ConsultaSeguimentoPergunta17TentaFazerQueOlheEnum,
} from './model-consultaSeguimentoPergunta17'
import {
  hasPergunta17TentaFazerQueOlheAnySim,
  isPergunta17TentaFazerQueOlheAllNao,
} from './utils-consultaSeguimentoPergunta17'

interface ConsultaSeguimentoPergunta17FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta17Model>
}

export function ConsultaSeguimentoPergunta17Field(props: Readonly<ConsultaSeguimentoPergunta17FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta17Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta17Model>({
    name,
    calculateResultado,
    conditionToCalculate,
  })

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_17}
          title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' tenta fazer com que você olhe para ele?' />}
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_17_HEADER} />}
        />
        {!isEmpty(value?.PERGUNTA_17_HEADER) && (
          <VFlow vSpacing={value?.PERGUNTA_17_HEADER === SimNaoEnum.SIM ? 0 : 1}>
            <ConsultaSeguimentoIndicatorStart />
            <VFlow>
              {value?.PERGUNTA_17_HEADER === SimNaoEnum.SIM && (
                <ConsultaSeguimentoIndicatorHelper
                  title='Por favor, me dê um exemplo de como ele tenta fazer com que você olhe para ele/ela.'
                  subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                />
              )}
              <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta17TentaFazerQueOlheEnum>
                name={name.PERGUNTA_17_TENTA_FAZER_QUE_OLHE}
                nameOutro={name.OUTRO}
                label='Ele...'
                description='Responda os exemplos até pontuar "Passou" ou "Falhou"'
                required
                options={PERGUNTA_17_TENTA_FAZER_QUE_OLHE_OPTIONS}
              />
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value?.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{
            FALHOU: 'Não para todos:',
            PASSOU: 'Sim para qualquer um:',
          }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_17_TENTA_FAZER_QUE_OLHE_OPTIONS: RadioOption<ConsultaSeguimentoPergunta17TentaFazerQueOlheEnum>[] = [
  {
    label: 'Diz "Olha!" ou "Olha para mim!"?',
    value: ConsultaSeguimentoPergunta17TentaFazerQueOlheEnum.FALA,
  },
  {
    label: 'Emite algum som ou faz barulho para você olhar o que ele está fazendo?',
    value: ConsultaSeguimentoPergunta17TentaFazerQueOlheEnum.EMITE_SOM_OU_BARULHO,
  },
  {
    label: 'Olha para você, para ganhar elogio ou comentário?',
    value: ConsultaSeguimentoPergunta17TentaFazerQueOlheEnum.OLHA_PARA,
  },
  {
    label: 'Continua olhando para ver se você está olhando?',
    value: ConsultaSeguimentoPergunta17TentaFazerQueOlheEnum.CONTINUA_OLHANDO,
  },
]

const calculateResultado = (values: ConsultaSeguimentoPergunta17Model) => {
  if (hasPergunta17TentaFazerQueOlheAnySim(values)) {
    return PassouFalhouEnum.PASSOU
  } else if (isPergunta17TentaFazerQueOlheAllNao(values)) {
    return PassouFalhouEnum.FALHOU
  }
}

const conditionToCalculate = (values: ConsultaSeguimentoPergunta17Model): boolean => {
  return hasPergunta17TentaFazerQueOlheAnySim(values) || isPergunta17TentaFazerQueOlheAllNao(values)
}
